import { PageDirectoryEntryName } from "../api/PageDirectory";
import { APDocumentType } from "../types/Document";
import { RoleName } from "../types/User";

const EmployerPageNameRestrictedPermissionMap: Record<
  EmployerPageName,
  Permission
> = {
  employerHome: "accessEmployer",
  employerApproveDrivers: "approveDossiers",
  employerInvoices: "viewInvoices",
  employerPackagingAgreements: "viewPackagingAgreements",
  employerPayrollAdvice: "viewPayrollAdvices",
  employerFBTReports: "viewFBTReports",
  employerEngagementReports: "viewEngagementReports",
  employerLuxuryLeaseReports: "viewLuxuryLeaseReports",
  employerPromotions: "viewPromotions",
  employerViewDossiers: "viewDossiers",
  employerTerminateDossier: "terminateDossiers",
  employerViewDossierTermination: "viewDossiers",
};

const PageNameToDocumentTypesMap: Record<DocumentPageName, APDocumentType[]> = {
  employerInvoices: ["Invoice", "Reconciliation Invoice", "Credit Note"],
  employerPackagingAgreements: ["Packaging Agreement"],
  employerPayrollAdvice: ["Payroll Advice", "Termination Advice"],
  employerFBTReports: ["FBT Report"],
  employerEngagementReports: ["Engagement Report"],
  employerLuxuryLeaseReports: ["Luxury Lease Report"],
  employerPromotions: ["Promotion"],
  dossierDocuments: [
    "Payroll Advice",
    "Packaging Agreement",
    "Termination Advice",
    "Insurance Certificate of Currency",
  ],
};

export type Permission =
  | "accessEmployer"
  | "viewInvoices"
  | "viewFBTReports"
  | "viewPayrollAdvices"
  | "viewReconciliationInvoices"
  | "viewTerminationAdvices"
  | "viewEngagementReports"
  | "viewPromotions"
  | "viewPackagingAgreements"
  | "viewLuxuryLeaseReports"
  | "accessDossiers"
  | "viewDossiers"
  | "viewDossierTerminations"
  | "approveDossiers"
  | "terminateDossiers"
  | "viewVehicleSettlementSurveys"
  | "viewEmployerSummaryStats";

export type AccountIdentity = {
  id: string;
  name: string;
  authorised: boolean;
  displayAnalytics: boolean;
};

export type AccountPermissions = Record<
  string,
  {
    account: AccountIdentity;
    roles: RoleName[];
    permissions: Permission[];
  }
>;

export const hasPermission = (
  accountPermissions: AccountPermissions,
  permission: Permission
) => {
  const allPermissions = Object.values(accountPermissions).flatMap(
    (account) => account.permissions
  );
  return allPermissions.includes(permission);
};

export function hasPermissionForAccount(
  accountPermissions: AccountPermissions,
  permission: Permission,
  accountId: string
): boolean {
  const account = Object.values(accountPermissions).find(
    (account) => account.account.id === accountId
  );
  return account?.permissions.includes(permission) ?? false;
}

export const filterAccountsByPermission = (
  accountPermissions: AccountPermissions,
  permission: Permission
): AccountIdentity[] => {
  return Object.values(accountPermissions)
    .filter((_accountPermissions) =>
      _accountPermissions.permissions.includes(permission)
    )
    .map((account) => account.account);
};

export const filterAccountsByAnalyticsPermission = (
  accountPermissions: AccountPermissions
): AccountIdentity[] => {
  const accountsWithAnalyticsPermission = filterAccountsByPermission(
    accountPermissions,
    "viewEmployerSummaryStats"
  );

  return accountsWithAnalyticsPermission.filter(
    (account) => account.displayAnalytics
  );
};

export const filterAccountsByEmployerPage = (
  accountPermissions: AccountPermissions,
  page: EmployerPageName
) => {
  return filterAccountsByPermission(
    accountPermissions,
    EmployerPageNameRestrictedPermissionMap[page]
  );
};

export type EmployerPageName = Extract<
  PageDirectoryEntryName,
  | "employerHome"
  | "employerApproveDrivers"
  | "employerInvoices"
  | "employerPackagingAgreements"
  | "employerPayrollAdvice"
  | "employerFBTReports"
  | "employerEngagementReports"
  | "employerLuxuryLeaseReports"
  | "employerPromotions"
  | "employerViewDossiers"
  | "employerTerminateDossier"
  | "employerViewDossierTermination"
>;

export type DossierPageName = Extract<
  PageDirectoryEntryName,
  "dossierDocuments"
>;

export type DocumentPageName =
  | Exclude<
      EmployerPageName,
      | "employerApproveDrivers"
      | "employerViewDossiers"
      | "employerHome"
      | "employerTerminateDossier"
      | "employerViewDossierTermination"
    >
  | DossierPageName;

export const isAuthorisedToViewEmployerPage = (
  accountPermissions: AccountPermissions,
  pageName: EmployerPageName
) => {
  return hasPermission(
    accountPermissions,
    EmployerPageNameRestrictedPermissionMap[pageName]
  );
};

export const getDocumentTypesForDocumentPage = (
  page: DocumentPageName
): APDocumentType[] => {
  return PageNameToDocumentTypesMap[page];
};
